import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { KpiResult } from '@masar/common/models';
import { functions } from '@masar/common/misc/functions';
import {
    formatKpiFixedNumber,
    fromKpiDurationSeconds,
    isKpiDurationUnit,
    kpiDurationPartsToSeconds,
    KpiDurationUnit,
    splitKpiDurationSeconds,
    toKpiDurationSeconds,
} from '@masar/common/utils';

@Component({
    selector: 'app-kpi-period-value-input',
    templateUrl: './kpi-period-value-input.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => KpiPeriodValueInputComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class KpiPeriodValueInputComponent implements ControlValueAccessor {
    @Input() public units: KpiResult['units'];
    @Input() public disabled = false;
    @Input() public decimalPlaces = 2;

    public hours: number | null = null;
    public minutes: number | null = null;
    public seconds: number | null = null;
    public displayValue = '';

    public get isDurationUnit(): boolean {
        return isKpiDurationUnit(this.units);
    }

    private value: number | null = null;
    private isSyncing = false;

    public constructor(private readonly cdr: ChangeDetectorRef) {}

    public writeValue(value: number | null | undefined): void {
        this.value = value ?? null;

        if (this.value === null) {
            this.resetParts();
        } else if (this.isDurationUnit) {
            this.syncDurationParts(this.value);
        } else {
            this.displayValue = formatKpiFixedNumber(
                this.value as number,
                this.decimalPlaces,
                {
                    thousandSeparator: true,
                    trimTrailingZeros: true,
                }
            );
        }
        this.cdr.markForCheck();
    }

    public registerOnChange(fn: (value: number | null) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.cdr.markForCheck();
    }

    public onNumericChange(raw: string): void {
        if ((raw ?? '').trim() === '') {
            this.displayValue = '';
            this.emit(null);
            return;
        }

        this.emit(functions.parseNumberFromString(raw));
    }

    public onDurationPartChange(): void {
        if (this.isSyncing) {
            return;
        }

        if (
            this.hours === null &&
            this.minutes === null &&
            this.seconds === null
        ) {
            this.emit(null);
            return;
        }

        this.emit(
            fromKpiDurationSeconds(
                kpiDurationPartsToSeconds({
                    hours: this.coercePart(this.hours),
                    minutes: this.coercePart(this.minutes),
                    seconds: this.coercePart(this.seconds),
                }),
                this.durationUnit
            )
        );
    }

    public handleBlur(): void {
        this.onTouched();
    }

    private get durationUnit(): KpiDurationUnit {
        return this.units as KpiDurationUnit;
    }

    private onChange: (value: number | null) => void = () => undefined;

    private onTouched: () => void = () => undefined;

    private syncDurationParts(value: number): void {
        this.isSyncing = true;
        const parts = splitKpiDurationSeconds(
            toKpiDurationSeconds(value, this.durationUnit),
            this.durationUnit
        );
        this.hours = parts.hours;
        this.minutes = parts.minutes;
        this.seconds = parts.seconds;
        this.isSyncing = false;
    }

    private resetParts(): void {
        this.isSyncing = true;
        this.hours = null;
        this.minutes = null;
        this.seconds = null;
        this.displayValue = '';
        this.isSyncing = false;
    }

    private emit(value: number | null): void {
        this.value = value;
        this.onChange(value);
    }

    private coercePart(value: number | string | null): number {
        if (value === null || value === '') {
            return 0;
        }
        const parsed = typeof value === 'number' ? value : parseInt(value, 10);
        if (!Number.isFinite(parsed) || parsed < 0) {
            return 0;
        }
        return Math.floor(parsed);
    }
}
